import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  requestNotificationPermission,
  getNotificationPermission,
  subscribeToPushNotifications,
  supportsNotifications,
} from "../utils/pwa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-retry interceptor: when server is sleeping/waking, retry up to 3 times
axios.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;
    if (!config || config._retryCount >= 3) return Promise.reject(error);
    
    // Retry on network errors or 502/503/504 (server waking up)
    const shouldRetry = !error.response || [502, 503, 504, 0].includes(error.response?.status);
    if (!shouldRetry) return Promise.reject(error);
    
    config._retryCount = (config._retryCount || 0) + 1;
    // Wait before retry: 2s, 4s, 6s
    await new Promise(resolve => setTimeout(resolve, config._retryCount * 2000));
    return axios(config);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("mes_token"));
  const [loading, setLoading] = useState(true);
  const [clockedIn, setClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Check clock-in status
      checkClockStatus();
      // Auto-subscribe push on session restore
      autoSubscribePush(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const autoSubscribePush = async (userData) => {
    if (!supportsNotifications() || !userData?.id) return;
    try {
      const permission = getNotificationPermission();
      if (permission === "granted") {
        await subscribeToPushNotifications(userData.id);
      } else if (permission === "default") {
        const granted = await requestNotificationPermission();
        if (granted) {
          await subscribeToPushNotifications(userData.id);
        }
      }
    } catch (e) {
      console.log("Push subscription skipped:", e);
    }
  };

  const checkClockStatus = async () => {
    try {
      const response = await axios.get(`${API}/time-tracking/status`);
      setClockedIn(response.data.clocked_in);
      if (response.data.clocked_in) {
        setClockInTime(response.data.clock_in);
      }
    } catch (error) {
      console.error("Error checking clock status:", error);
    }
  };

  const clockIn = async () => {
    try {
      const response = await axios.post(`${API}/time-tracking/clock-in`);
      setClockedIn(true);
      setClockInTime(response.data.clock_in);
      toast.success("Entrada registada com sucesso!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao registar entrada";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const clockOut = async () => {
    try {
      const response = await axios.post(`${API}/time-tracking/clock-out`);
      setClockedIn(false);
      setClockInTime(null);
      const hours = Math.floor(response.data.total_minutes / 60);
      const mins = response.data.total_minutes % 60;
      toast.success(`Saída registada! Tempo total: ${hours}h ${mins}min`);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao registar saída";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const login = async (userCode, pin) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        user_code: userCode,
        pin: pin,
      });
      
      const { user: userData, token: authToken } = response.data;
      
      localStorage.setItem("mes_token", authToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
      
      setToken(authToken);
      setUser(userData);
      
      // Auto clock-in after login
      try {
        const clockResponse = await axios.post(`${API}/time-tracking/clock-in`);
        setClockedIn(true);
        setClockInTime(clockResponse.data.clock_in);
      } catch (e) {
        // Already clocked in, just update status
        checkClockStatus();
      }
      
      // Auto-subscribe push notifications
      autoSubscribePush(userData);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao fazer login";
      return { success: false, error: message };
    }
  };

  const loginWithQr = async (qrToken) => {
    try {
      const response = await axios.post(`${API}/auth/qr-login`, { qr_token: qrToken });
      const authToken = response.data.access_token;
      const userData = {
        id: response.data.user_id,
        user_code: response.data.user_code,
        name: response.data.name,
        role: response.data.role,
        photo_url: response.data.photo_url,
      };
      localStorage.setItem("mes_token", authToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
      setToken(authToken);
      setUser(userData);
      try {
        const clockResponse = await axios.post(`${API}/time-tracking/clock-in`);
        setClockedIn(true);
        setClockInTime(clockResponse.data.clock_in);
      } catch (e) {
        checkClockStatus();
      }
      autoSubscribePush(userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || "QR inválido" };
    }
  };

  const register = async (userCode, pin, name, role, shift = "morning") => {
    try {
      await axios.post(`${API}/auth/register`, {
        user_code: userCode,
        pin: pin,
        name: name,
        role: role,
        shift: shift,
      });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao registar";
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    // Clock out before logout if clocked in
    if (clockedIn) {
      try {
        await axios.post(`${API}/time-tracking/clock-out`);
      } catch (e) {
        console.error("Error clocking out:", e);
      }
    }
    
    localStorage.removeItem("mes_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setClockedIn(false);
    setClockInTime(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        loginWithQr,
        register,
        logout,
        clockIn,
        clockOut,
        clockedIn,
        clockInTime,
        isAdmin: user?.role === "admin",
        isOperator: user?.role === "operator",
        isQuality: user?.role === "quality",
        isShiftLeader: user?.role === "shift_leader",
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
